import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { formatINR } from "@milqit/ui";
import { SUCCESS_MESSAGES } from "@milqit/messages";
import {
  ADMIN_EVENT_TYPES,
  ADMIN_TICKET_EVENT_TYPES,
  type AdminEventType,
  TICKET_PRIORITIES,
} from "@milqit/shared-types";
import { toast } from "sonner";
import { z } from "zod";

import { apiUrl, renewSession } from "./api/client";
import { roots } from "./queries";
import { announceNewOrder, isOrderSoundOn, loadVoices } from "./order-sound";

const orderEvent = z.object({
  type: z.enum(ADMIN_EVENT_TYPES),
  orderId: z.string(),
  orderNumber: z.string(),
  customerName: z.string(),
  total: z.number(),
  status: z.string(),
  area: z.string(),
  at: z.string(),
});

const ticketEvent = z.object({
  type: z.enum(ADMIN_TICKET_EVENT_TYPES),
  ticketId: z.string(),
  ticketNumber: z.string(),
  subject: z.string(),
  customerName: z.string(),
  priority: z.enum(TICKET_PRIORITIES),
  at: z.string(),
});

const TITLES: Record<AdminEventType, (orderNumber: string) => string> = {
  "order.confirmed": SUCCESS_MESSAGES.order.newOrder,
  "order.cancelled": SUCCESS_MESSAGES.order.cancelled,
  "order.payment_failed": SUCCESS_MESSAGES.order.paymentFailed,
};

/** Screens whose data an order event changes. */
const REFRESHED = [
  roots.orders,
  roots.order,
  roots.deliveries,
  roots.dashboard,
  roots.navCounts,
  roots.liveOrders,
];
/** Screens a new support ticket changes. */
const REFRESHED_BY_TICKETS = [roots.tickets, roots.navCounts];

/**
 * Live order alerts for the whole dashboard: a toast (and, unless staff turned order sounds off, a
 * chime and "मिल्किट पे नया ऑर्डर आया है" for new orders) and fresh order, delivery and dashboard
 * data. EventSource reconnects by itself if the connection drops; while the browser is offline the
 * stream is closed and reopened when it's back.
 */
export function useLiveOrderAlerts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    loadVoices();
    let stopped = false;
    let lastRenewal = 0;
    const onEvent = (message: MessageEvent<string>) => {
      const parsed = orderEvent.safeParse(JSON.parse(message.data));
      if (!parsed.success) return;
      const event = parsed.data;
      for (const queryKey of REFRESHED) void queryClient.invalidateQueries({ queryKey });
      const show = event.type === "order.confirmed" ? toast.success : toast.warning;
      show(TITLES[event.type](event.orderNumber), {
        description: `${formatINR(event.total)} · ${event.customerName} · ${event.area}`,
        action: { label: "View", onClick: () => navigate(`/orders/${event.orderId}`) },
      });
      if (event.type === "order.confirmed" && isOrderSoundOn()) announceNewOrder();
    };
    const onTicket = (message: MessageEvent<string>) => {
      const parsed = ticketEvent.safeParse(JSON.parse(message.data));
      if (!parsed.success) return;
      const event = parsed.data;
      for (const queryKey of REFRESHED_BY_TICKETS) void queryClient.invalidateQueries({ queryKey });
      toast.info(SUCCESS_MESSAGES.ticket.newTicket(event.ticketNumber), {
        description: `${event.customerName} · ${event.subject}`,
        action: { label: "View", onClick: () => navigate(`/support/${event.ticketId}`) },
      });
    };
    let source: EventSource | null = null;
    const open = () => {
      source?.close();
      const stream = new EventSource(apiUrl("/admin/events"), { withCredentials: true });
      source = stream;
      for (const type of ADMIN_EVENT_TYPES) stream.addEventListener(type, onEvent);
      for (const type of ADMIN_TICKET_EVENT_TYPES) stream.addEventListener(type, onTicket);
      // EventSource retries dropped connections itself, but gives up for good when the server
      // refuses one — typically an access cookie that expired while the stream was open. Renew it
      // and reopen, at most once a minute so a real permission problem can't loop.
      stream.onerror = () => {
        if (stream.readyState !== EventSource.CLOSED || Date.now() - lastRenewal < 60_000) return;
        lastRenewal = Date.now();
        void renewSession().then((ok) => {
          if (ok && !stopped) open();
        });
      };
    };
    // Offline, EventSource would retry every few seconds and fill the console; wait for the
    // connection to come back, then reconnect once.
    const goOffline = () => {
      source?.close();
      source = null;
    };
    const goOnline = () => {
      if (!stopped) open();
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    if (navigator.onLine) open();
    return () => {
      stopped = true;
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
      source?.close();
    };
  }, [queryClient, navigate]);
}
